<template>
  <section id="quick-quote" class="relative py-32 md:py-40 overflow-hidden">
    <!-- Background -->
    <div class="absolute inset-0 bg-gray-950">
      <img src="/ciobai_hero_background.webp" alt="" class="absolute inset-0 w-full h-full object-cover opacity-60" />
    </div>

    <div class="container mx-auto px-4 relative z-10">
      <div class="max-w-3xl mx-auto text-center">
        <h2 class="text-4xl md:text-6xl font-bold text-white tracking-tight leading-[1.1] mb-6">
          {{ t('cta.ready') }}
        </h2>
        <p class="text-lg md:text-xl text-gray-300 mb-4 max-w-2xl mx-auto leading-relaxed font-medium">
          {{ t('cta.description') }}
        </p>
        <p class="text-sm text-gray-500 mb-10 max-w-xl mx-auto">
          {{ t('cta.subdescription') }}
        </p>

        <div class="inline-flex items-center gap-2 rounded-full bg-white/[0.06] border border-white/[0.08] px-4 py-1.5 text-[13px] font-medium text-gray-300 mb-10 backdrop-blur-sm">
          <span class="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse"></span>
          {{ t('cta.specialOffer') }}
        </div>

        <div class="flex flex-col sm:flex-row gap-3 justify-center items-center mb-10">
          <router-link to="/contact" class="inline-flex items-center h-14 px-8 bg-white text-gray-900 font-bold text-[15px] rounded-xl hover:bg-gray-100 transition-colors group">
            {{ t('cta.button') }}
            <ArrowRight class="ml-2.5 h-4 w-4 transition-transform group-hover:translate-x-1" />
          </router-link>
          
          <button @click="showQuickForm = !showQuickForm" class="inline-flex items-center h-14 px-8 text-gray-400 hover:text-white font-medium text-[15px] rounded-xl border border-white/[0.08] hover:border-white/20 transition-all">
            {{ t('cta.quickForm') }}
          </button>
        </div>
        
        <div class="flex flex-wrap justify-center gap-6 text-[13px] text-gray-400 font-medium">
          <span class="flex items-center gap-2">
            <Check class="h-3.5 w-3.5 text-green-400" />
            {{ t('cta.trust.free') }}
          </span>
          <span class="flex items-center gap-2">
            <Check class="h-3.5 w-3.5 text-green-400" />
            {{ t('cta.trust.noObligation') }}
          </span>
          <span class="flex items-center gap-2">
            <Check class="h-3.5 w-3.5 text-green-400" />
            {{ t('cta.trust.response') }}
          </span>
        </div>
      </div>

      <transition name="slide-down">
        <div v-if="showQuickForm" class="max-w-md mx-auto mt-12 rounded-2xl bg-white/[0.04] border border-white/[0.06] backdrop-blur-sm p-8">
          <h3 class="text-xl font-bold mb-6 text-center text-white">{{ t('cta.quickFormTitle') }}</h3>
          <form @submit.prevent="handleQuickSubmit" class="space-y-3">
            <input 
              v-model="quickForm.email" 
              type="email" 
              :placeholder="t('cta.form.email')"
              required
              class="w-full px-4 py-3 bg-white/[0.04] border border-white/[0.08] text-white placeholder-gray-600 rounded-xl focus:outline-none focus:border-white/20 text-sm"
            />
            <input 
              v-model="quickForm.company" 
              type="text" 
              :placeholder="t('cta.form.company')"
              class="w-full px-4 py-3 bg-white/[0.04] border border-white/[0.08] text-white placeholder-gray-600 rounded-xl focus:outline-none focus:border-white/20 text-sm"
            />
            <button 
              type="submit" 
              :disabled="isSubmitting"
              class="w-full h-12 bg-white text-gray-900 rounded-xl font-semibold text-[15px] hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span v-if="!isSubmitting">{{ t('cta.form.submit') }}</span>
              <span v-else>{{ t('cta.form.sending') }}</span>
            </button>
          </form>
        </div>
      </transition>
    </div>
  </section>
</template>

<script setup>
import { ref } from 'vue'
import { ArrowRight, Check } from 'lucide-vue-next'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const showQuickForm = ref(false)
const isSubmitting = ref(false)
const quickForm = ref({ email: '', company: '' })

const handleQuickSubmit = async () => {
  try {
    isSubmitting.value = true
    const response = await fetch('https://formspree.io/f/mkgvzeed', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type: 'quick-contact', ...quickForm.value })
    })
    if (response.ok) {
      alert(t('cta.form.success'))
      quickForm.value = { email: '', company: '' }
      showQuickForm.value = false
    } else {
      throw new Error('Form submission failed')
    }
  } catch (error) {
    alert(t('cta.form.error'))
  } finally {
    isSubmitting.value = false
  }
}
</script>

<style scoped>
.slide-down-enter-active, .slide-down-leave-active { transition: all 0.3s ease; }
.slide-down-enter-from, .slide-down-leave-to { opacity: 0; transform: translateY(-10px); }
</style>
